// Axios-клиент с Bearer interceptor + 401-handler с loop-protection.
// Source of truth: docs/specs/sprint-3/stage-1-contracts.md §5.

import axios, { AxiosError, type AxiosInstance } from 'axios';
import { getToken, clearToken } from '@/lib/auth-storage';
import { clearMarkerNavState } from '@/lib/markerNavState';
import { router } from '@/lib/router';
import { isMaintenanceResponse, maintenance } from '@/lib/maintenance';

const baseURL = `${import.meta.env.VITE_API_URL}/api/v1`;

export const api: AxiosInstance = axios.create({
  baseURL,
  headers: { Accept: 'application/json' },
  withCredentials: false, // Bearer-only, не SPA cookie (Sprint 1 D-11)
  timeout: 20000,
});

api.interceptors.request.use(async (config) => {
  // Офлайн отвечаем сразу, не дожидаясь таймаута в 20 секунд.
  //
  // Без этого в авиарежиме кнопка «Войти» крутилась до срабатывания таймаута,
  // и всё это время человек не видел ни одного объяснения: WKWebView запрос
  // не отбивает мгновенно, а просто держит. Ошибка без `response` — ровно то,
  // что useApiErrorToast уже раскладывает в «Нет соединения».
  //
  // navigator.onLine в общем случае врёт (он про «подключён к сети», а не
  // «интернет работает»), но авиарежим ловит точно — именно его и ловим.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return Promise.reject(
      new AxiosError('Network Error', AxiosError.ERR_NETWORK, config as never, null),
    );
  }

  const token = await getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => {
    // Сервер ответил — окна техработ больше нет (или к нему пустили по секрету).
    maintenance.leave();
    return res;
  },
  async (err: AxiosError) => {
    // HEAL-86 (§8.1): окно техработ — экран «Технические работы» вместо ошибки на
    // каждом экране. Ошибку всё равно пробрасываем: экраны под флагом уже не видны.
    if (isMaintenanceResponse(err.response?.status, err.response?.data)) {
      maintenance.enter();
    }
    if (err.response?.status === 401) {
      await clearToken();
      clearMarkerNavState(); // HEAL-9: смена сессии не должна тащить чужой nav-state
      const path = window.location.pathname;
      if (
        !path.startsWith('/signin') &&
        !path.startsWith('/signup') &&
        !path.startsWith('/auth/')
      ) {
        router.push('/signin');
      }
    }
    return Promise.reject(err);
  },
);
